import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, ExternalLink, Sparkles, Clock, CheckCircle2,
  XCircle, AlertCircle, FileText, Image as ImageIcon, ListOrdered
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';


const STORY_STATUS = {
  pending:        { colour: 'bg-slate-100 text-slate-600 border-slate-300',           label: 'Not generated',  icon: Clock },
  in_progress:    { colour: 'bg-blue-50 text-blue-600 border-blue-200',               label: 'Generating',     icon: Loader2 },
  complete:       { colour: 'bg-emerald-50 text-emerald-700 border-emerald-200',      label: 'Story created',  icon: CheckCircle2 },
  failed:         { colour: 'bg-red-50 text-red-600 border-red-200',                  label: 'Failed',         icon: XCircle },
  rewrite_failed: { colour: 'bg-amber-50 text-amber-700 border-amber-200',            label: 'Rewrite failed', icon: AlertCircle },
};

const QUEUED_BADGE = {
  colour: 'bg-blue-50 text-blue-700 border-blue-200',
  label:  'Queued',
  icon:   ListOrdered,
};

const fmtUK = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

const FILTERS = [
  { id: 'all',            key: 'all',            label: 'All' },
  { id: 'pending',        key: 'pending',        label: 'Not generated' },
  { id: 'queued',         key: 'queued',         label: 'Queue' },
  { id: 'complete',       key: 'complete',       label: 'Generated' },
  { id: 'rewrite_failed', key: 'rewrite_failed', label: 'Failed' },
];


function StoryStatusBadge({ item }) {
  const cfg = item.is_queued
    ? QUEUED_BADGE
    : (STORY_STATUS[item.story_creation_status] || STORY_STATUS.pending);
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold border ${cfg.colour}`}>
      <Icon size={10} className={item.story_creation_status === 'in_progress' && !item.is_queued ? 'animate-spin' : ''} />
      {cfg.label}
    </span>
  );
}


function ItemRow({ item, onGenerate, onView }) {
  const [busy, setBusy] = useState(false);
  const canGenerate = ['pending', 'rewrite_failed', 'failed'].includes(item.story_creation_status);

  const handleGenerate = async () => {
    setBusy(true);
    try { await onGenerate(item); } finally { setBusy(false); }
  };

  return (
    <div className={`border rounded-xl p-4 transition-colors shadow-sm ${
      item.is_queued
        ? 'bg-blue-50/40 border-blue-200 hover:border-blue-300'
        : 'bg-white border-slate-200 hover:border-slate-300'
    }`}>
      <div className="flex items-start gap-3">
        {item.source_image_url && (
          <img src={item.source_image_url} alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="w-20 h-14 object-cover rounded shrink-0 bg-slate-100" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 mb-1">
            <h3 className="text-slate-900 font-medium leading-snug flex-1">{item.title}</h3>
            <StoryStatusBadge item={item} />
          </div>

          {item.summary && (
            <p className="text-xs text-slate-500 line-clamp-2 mt-1">{item.summary}</p>
          )}

          <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400 flex-wrap">
            {item.source_published_at && (
              <span>{fmtUK(item.source_published_at)}</span>
            )}
            {item.output_count > 0 && (
              <span className="text-emerald-600 inline-flex items-center gap-1">
                <FileText size={11} /> {item.output_count} {item.output_count === 1 ? 'story' : 'stories'}
              </span>
            )}
            {!item.source_image_url && (
              <span className="text-amber-500 inline-flex items-center gap-1">
                <ImageIcon size={11} /> No image
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <a href={item.source_url} target="_blank" rel="noopener noreferrer"
            className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors" title="Open source">
            <ExternalLink size={14} />
          </a>
          <button onClick={() => onView(item)}
            className="px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors">
            Details
          </button>
          {canGenerate && (
            <button onClick={handleGenerate} disabled={busy}
              title={item.is_queued ? 'Generate now, ahead of the queue' : 'Generate this story'}
              className={`flex items-center gap-1.5 disabled:opacity-50 text-white px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                item.is_queued ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}>
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {item.is_queued ? 'Generate now' : 'Generate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


export default function SourceItemsTab({ sourceId }) {
  const navigate = useNavigate();

  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [counts, setCounts]     = useState({});
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [offset, setOffset]     = useState(0);
  const limit = 25;

  useEffect(() => { setOffset(0); }, [search, statusFilter]);
  useEffect(() => { load(); }, [search, statusFilter, offset, sourceId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { source_id: sourceId, limit, offset };
      if (search) params.search = search;
      if (statusFilter === 'queued')                            params.queued = true;
      else if (statusFilter !== 'all')                          params.story_status = statusFilter;
      const { data } = await api.get('/feeds/items', { params });
      setItems(data.items);
      setTotal(data.total);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (item) => {
    try {
      const { data } = await api.post(`/feeds/items/${item.id}/generate-stories`);
      const created = data.stories_created || 0;
      const failed  = data.failed || 0;
      const skipped = data.skipped_idempotent || 0;

      if (created > 0 && failed === 0) {
        toast.success(`Generated ${created} ${created === 1 ? 'story' : 'stories'}`);
      } else if (created > 0 && failed > 0) {
        toast.warning(`Generated ${created}, failed ${failed}`);
      } else if (skipped > 0 && created === 0 && failed === 0) {
        toast.info('Stories already exist');
      } else if (failed > 0) {
        const firstError = data.results?.find(
          r => !r.success && !r.error?.startsWith('story already')
        )?.error || 'Generation failed';
        toast.error(firstError);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search items..."
            className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500" />
        </div>

        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
          {FILTERS.map(t => (
            <button key={t.id} onClick={() => setStatusFilter(t.id)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors inline-flex items-center gap-1.5 ${
                statusFilter === t.id
                  ? 'bg-white text-blue-600 border border-slate-200 shadow-sm'
                  : 'text-slate-500 hover:text-slate-900'
              }`}>
              {t.label}
              {counts[t.key] > 0 && (
                <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center ${
                  statusFilter === t.id ? 'bg-blue-50 text-blue-600' : 'bg-slate-200 text-slate-600'
                }`}>
                  {counts[t.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading...
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">
          {statusFilter === 'queued'
            ? 'Nothing from this source is waiting in the queue.'
            : search || statusFilter !== 'all'
              ? 'No items match your filters.'
              : 'No items yet. Use "Fetch now" above to ingest items.'}
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-3">
            Showing {offset + 1}–{Math.min(offset + items.length, total)} of {total} · times in UK
          </p>

          <div className="space-y-2">
            {items.map(item => (
              <ItemRow key={item.id} item={item}
                onGenerate={handleGenerate}
                onView={(i) => navigate(`/feeds/items/${i.id}`)} />
            ))}
          </div>

          {total > limit && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <button onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200">
                Previous
              </button>
              <span className="text-xs text-slate-400">
                Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
              </span>
              <button onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= total}
                className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed text-slate-600 text-sm rounded border border-slate-200">
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}